// src/components/ErrorBoundary.jsx
// Extrait de GerantClientUI.jsx — Option B refactorisation (session v86)
// Contient : getRapportStructure, ErrorBoundary, RapportModal

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { compressToDataUrl, dataUrlSizeKB, now, photoLog, photoLogClear, today, supplementsNonCoches } from '../utils.js';
import { uploadPhotosToStorage } from '../supabase.js';
import { enqueueRapport } from '../localQueue.js';
import { suspendRealtime, resumeRealtime } from '../realtime.js';
import { useGeo } from './Pointage.jsx';
import { generateRapportPdf } from '../pdf-utils.js';
import { TT, CL, GP } from '../constants.js';
import { Ic } from './Ic.jsx';

export function getRapportStructure(bien){
  const useNew=!!bien?.mrOn&&(bien?.mr||[]).length>0;
  if(useNew){
    const items=[],photoItems=[],genPhotos=[],sections=[],guideSteps=[];
    (bien.mr||[]).forEach((rm,rmIdx)=>{
      const rmItems=[];
      (rm.items||[]).forEach((it,itIdx)=>{
        // Cas spécial : chambre avec plusieurs lits → on duplique l'item "Lit refait" autant de fois
        const isLit=rm.type==='chambre'&&/lit/i.test(it.lb||'');
        const nbLits=rm.cfg?.nbLits||1;
        // Émet un item : alimente TOUJOURS guideSteps (séquence du mode guidé, SPEC "panneau
        // de configuration" — ordre/temps/totalité des tâches), et alimente items/rmItems/
        // photoItems UNIQUEMENT si non horsRapport (le rapport classique, la facturation et le
        // contrôle qualité ne voient jamais les tâches formation-only).
        // ordreGuide explicite sinon repli sur l'ordre naturel pièce/item (rétrocompat totale :
        // aucun bien existant n'a ordreGuide renseigné, donc comportement inchangé par défaut).
        const emit=(dup,subIdx)=>{
          const fallback=rmIdx*1000+itIdx*10+subIdx;
          guideSteps.push({...dup,_rmId:rm.id,_rmName:rm.nom||rm.lb,_rmEmoji:rm.emoji,_ordreGuide:(typeof dup.ordreGuide==='number'?dup.ordreGuide:fallback)});
          if(!dup.horsRapport){
            rmItems.push(dup);items.push(dup);
            if(dup.photo)photoItems.push({...dup,_rmName:rm.nom||rm.lb,_rmEmoji:rm.emoji});
          }
        };
        if(isLit&&nbLits>1){
          for(let i=1;i<=nbLits;i++){
            const lb=it.lb.replace(/lit/i,m=>m+' '+i);
            emit({...it,id:`${it.id}_l${i}`,lb},i);
          }
        }else{
          emit(it,0);
        }
      });
      sections.push({key:rm.id,title:`${rm.emoji||'📦'} ${rm.nom||rm.lb}`,items:rmItems,room:rm});
      // Photos générales pour cette pièce (0 à 3) — alimentent aussi guideSteps (étape "prendre
      // la/les photo(s)" à sa place naturelle dans la séquence, juste après les points de la pièce).
      const nPh=Number(rm.photo)||(rm.photo===true?1:0);
      for(let i=0;i<nPh;i++){
        const g={id:`${rm.id}_g${i}`,l:nPh>1?`Photo ${i+1}`:'Photo générale',_rmId:rm.id,_rmName:rm.nom||rm.lb,_rmEmoji:rm.emoji,req:!!rm.photoReq};
        genPhotos.push(g);
        guideSteps.push({...g,_genPhoto:true,_rmId:rm.id,_rmName:rm.nom||rm.lb,_rmEmoji:rm.emoji,_ordreGuide:rmIdx*1000+999+i});
      }
    });
    guideSteps.sort((a,b)=>a._ordreGuide-b._ordreGuide);
    return{items,photoItems,genPhotos,sections,guideSteps,useNew:true};
  }
  // Ancien mode
  const allCL=[...CL,...(bien?.ec||[])];
  const cats=[...new Set(allCL.map(c=>c.cat))];
  return{
    items:allCL,
    photoItems:allCL.filter(c=>c.pk).map(c=>({...c,_photoLabel:c.pl||c.lb})),
    genPhotos:GP.map(p=>({id:p.id,l:p.l})),
    sections:cats.map(cat=>({key:cat,title:cat,items:allCL.filter(c=>c.cat===cat)})),
    guideSteps:[],
    useNew:false
  };
}

// ─── ErrorBoundary : empêche qu'une exception dans un composant enfant (ex. RapportModal)
// ne démonte tout le rendu React et provoque une page blanche / sortie de l'appli.
// En cas d'erreur, affiche un message lisible au lieu de planter, et log l'erreur en console
// (visible via la console Safari branchée au Mac, pour diagnostiquer la cause exacte).
//
// Deux usages :
//  - AUTOUR D'UNE MODALE (prop onClose) : message court + bouton Fermer. Comportement historique.
//  - À LA RACINE (main.jsx, sans onClose) : ajouté le 28/07/2026. Jusque-là la racine n'était
//    protégée par rien — une erreur de rendu ou de phase de commit laissait un écran blanc et,
//    en console, une pile entièrement interne à React sans aucun de nos fichiers. React fournit
//    pourtant la pile des COMPOSANTS via info.componentStack : c'est le seul moyen de savoir
//    lequel est fautif. Nécessite esbuild keepNames (vite.config.js), sinon les noms sont mangés.
export class ErrorBoundary extends React.Component{
  constructor(props){super(props);this.state={hasError:false,msg:'',stack:''};}
  static getDerivedStateFromError(error){return{hasError:true,msg:(error&&error.message)||'Erreur inconnue'};}
  componentDidCatch(error,info){
    const stack=(info&&info.componentStack)||'';
    console.error('[ErrorBoundary] exception capturée',error,info);
    console.error('[ErrorBoundary] pile des composants :',stack);
    // Persistance : survit au rechargement et à la fermeture de l'onglet, donc exploitable
    // même si la console n'était pas ouverte au moment du plantage.
    try{
      const prev=JSON.parse(localStorage.getItem('react_crash')||'[]');
      prev.push({at:new Date().toISOString(),msg:String((error&&error.message)||error),stack:stack.slice(0,3000)});
      localStorage.setItem('react_crash',JSON.stringify(prev.slice(-5)));
    }catch(_){}
    this.setState({stack});
  }
  render(){
    if(this.state.hasError){
      // Mode racine : pas de onClose, donc rien à fermer — il faut recharger, et on affiche
      // le détail technique complet pour pouvoir le transmettre.
      if(!this.props.onClose){
        const txt=`${this.state.msg}\n\nPile des composants :${this.state.stack}`;
        return(<div style={{padding:20,fontFamily:'system-ui,sans-serif',maxWidth:760,margin:'40px auto'}}>
          <div style={{background:'#fff',border:'2px solid #d32f2f',borderRadius:12,padding:'18px 20px'}}>
            <div style={{fontWeight:700,fontSize:17,color:'#b71c1c',marginBottom:6}}>⚠️ L'application a rencontré une erreur</div>
            <div style={{fontSize:13.5,color:'#444',lineHeight:1.55,marginBottom:14}}>
              Rien n'est perdu : le travail déjà enregistré est conservé. Rechargez la page pour reprendre.
              Si le problème revient, copiez le détail ci-dessous et transmettez-le à la direction.
            </div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:14}}>
              <button onClick={()=>window.location.reload()} style={{padding:'9px 16px',borderRadius:8,border:'none',background:'#d32f2f',color:'#fff',fontWeight:600,fontSize:14,cursor:'pointer'}}>Recharger la page</button>
              <button onClick={()=>{try{navigator.clipboard.writeText(txt);}catch(_){}}} style={{padding:'9px 16px',borderRadius:8,border:'1px solid #bbb',background:'#fff',fontWeight:600,fontSize:14,cursor:'pointer'}}>Copier le détail</button>
            </div>
            <pre style={{background:'#fafafa',border:'1px solid #e0e0e0',borderRadius:8,padding:12,fontSize:11,lineHeight:1.45,overflow:'auto',maxHeight:320,whiteSpace:'pre-wrap',margin:0}}>{txt}</pre>
          </div>
        </div>);
      }
      return(<div style={{padding:'20px',background:'#fde8e8',border:'1.5px solid #f5b8b8',borderRadius:10,margin:10}}>
        <div style={{fontWeight:700,fontSize:15,color:'var(--dg)',marginBottom:8}}>⚠️ Un problème est survenu</div>
        <div style={{fontSize:13,color:'var(--ink)',lineHeight:1.6,marginBottom:10}}>
          L'écran n'a pas pu s'afficher correctement, mais l'application n'a pas planté. Ton travail enregistré n'est pas perdu.<br/>
          Ferme cette fenêtre et réessaie. Si le problème revient, fais une capture de ce message et envoie-la à la direction.
        </div>
        <div style={{fontSize:11,color:'var(--si)',fontFamily:'monospace',background:'#fff',padding:'8px 10px',borderRadius:6,wordBreak:'break-word'}}>
          Détail technique : {this.state.msg}
        </div>
        <div style={{marginTop:12,textAlign:'right'}}>
          <button onClick={this.props.onClose} className="btn bgh">Fermer</button>
        </div>
      </div>);
    }
    return this.props.children;
  }
}

export function RapportModal({task,bien,sid,existing,onSave,onClose,authorLabel=''}){
  const struct=useMemo(()=>getRapportStructure(bien),[bien]);
  const {items:allItems,photoItems,genPhotos,sections,useNew}=struct;
  const[cks,setCks]=useState(existing?.cks||{});
  const[pk,setPk]=useState(existing?.pk||{});
  const[gp,setGp]=useState(existing?.gp||{});
  const[bill,setBill]=useState(existing?.bill||{});
  const[notes,setNotes]=useState(existing?.notes||'');
  const[step,setStep]=useState(0);
  // triedSubmit : passe à true au premier clic sur "Soumettre". Tant qu'il est false,
  // les indicateurs rouges d'obligatoires non remplis restent cachés (pour ne pas
  // signaler au salarié quels items sont obligatoires avant qu'il essaie de soumettre).
  const[triedSubmit,setTriedSubmit]=useState(false);
  // Engagement salarié (v159, 14/08/2026, demande Claire) : exigé uniquement à la toute
  // première soumission (existing?.sub encore vide) — voir save() plus bas pour le report
  // de ce champ sur les réécritures ultérieures (brouillon, correction admin…).
  const[engageRapport,setEngageRapport]=useState(false);
  const firstSubmission=!existing?.sub;
  // Garde contre les double-clics et permet d'afficher "Envoi…" pendant l'upload des photos vers Storage.
  const[saving,setSaving]=useState(false);
  const checked=Object.values(cks).filter(Boolean).length;
  const pct=allItems.length?Math.round((checked/allItems.length)*100):0;
  // Calcule la liste des obligations non remplies (items et photos obligatoires).
  // Retourne {items:[{id,lb,_rmName,_rmEmoji,reason}], genPhotos:[{id,l,_rmName,_rmEmoji}]}
  // - "coche manquante" pour les items req non cochés
  // - "photo manquante" pour les items req+photo (ou photoItems req) sans photo
  // - les photos générales (req via photoReq sur la pièce) non prises
  const missing=useMemo(()=>{
    const items=[],photos=[],genPh=[];
    sections.forEach(sec=>{
      sec.items.forEach(it=>{
        if(!it.req)return;
        const ck=!!cks[it.id];
        const ph=it.photo;
        const hasPh=!!pk[it.id];
        if(!ck) items.push({id:it.id,lb:it.lb,_rmName:sec.room?.nom||sec.title,_rmEmoji:sec.room?.emoji||'📦',reason:'coche'});
        else if(ph&&!hasPh) photos.push({id:it.id,lb:it.lb,_rmName:sec.room?.nom||sec.title,_rmEmoji:sec.room?.emoji||'📦',reason:'photo'});
      });
    });
    // Photos générales de pièce obligatoires
    genPhotos.forEach(gph=>{
      if(gph.req&&!gp[gph.id]) genPh.push(gph);
    });
    return{items,photos,genPh,total:items.length+photos.length+genPh.length};
  },[sections,genPhotos,cks,pk,gp]);
  // Capture photo via input file natif (sur mobile : caméra arrière directe).
  // Si type='pk' (photo liée à un item) → la prise de photo coche aussi l'item automatiquement.
  const cap=(type,id)=>{
    const inp=document.createElement('input');
    inp.type='file';inp.accept='image/*';inp.capture='environment';
    // Sécurité caméra (Android/MIUI) : on ferme le WebSocket Realtime AVANT d'ouvrir l'appareil
    // photo, pour alléger ce que l'onglet doit maintenir pendant sa suspension. On le rouvre au
    // retour. Filet : si l'utilisateur annule la prise (pas d'onchange), le retour de focus relance.
    let _resumed=false;
    const doResume=()=>{if(_resumed)return;_resumed=true;setTimeout(()=>resumeRealtime(),1200);window.removeEventListener('focus',onFocusBack);};
    const onFocusBack=()=>{setTimeout(doResume,1500);};
    inp.onchange=async e=>{
      const file=e.target.files&&e.target.files[0];
      if(!file){doResume();return;}
      photoLog('Photo sélectionnée',`${Math.round(file.size/1024)} Ko · ${file.type||'?'} · point ${id}`);
      const apply=u=>{
        photoLog('Compression OK',`${dataUrlSizeKB(u)} Ko finaux`);
        if(type==='pk'){setPk(p=>({...p,[id]:u}));setCks(c=>({...c,[id]:true}));}
        else setGp(p=>({...p,[id]:u}));
        photoLog('Photo enregistrée');photoLogClear();
        doResume();
      };
      try{
        photoLog('Décodage + compression démarré (1280/0.7)');
        const u=await compressToDataUrl(file,{maxDim:1280,quality:0.7});
        apply(u);
      }catch(err){
        photoLog('1ère compression échouée',(err&&err.message)||String(err));
        await new Promise(r=>setTimeout(r,400)); // laisse la mémoire respirer avant de retenter
        try{
          photoLog('2e tentative dégradée (800/0.45)');
          const u2=await compressToDataUrl(file,{maxDim:800,quality:0.45});
          apply(u2);
        }catch(err2){
          photoLog('2e compression échouée',(err2&&err2.message)||String(err2));
          window.alert('La photo n\'a pas pu être traitée par le téléphone (format ou taille). Réessaie de la reprendre. Si ça recommence, préviens la direction.');
          doResume();
        }
      }
    };
    // Suspend le Realtime puis ouvre la caméra. Le focus revient à la fermeture de la caméra.
    window.addEventListener('focus',onFocusBack);
    suspendRealtime();
    inp.click();
  };
  const delPhoto=(type,id)=>{
    if(type==='pk')setPk(p=>{const n={...p};delete n[id];return n;});
    else setGp(p=>{const n={...p};delete n[id];return n;});
  };
  const{req}=useGeo();
  // Re-compresse les photos trop volumineuses avant soumission (filet de sécurité pour les anciennes photos non compressées).
  // Limite cible : 200 KB par photo (largement sous la limite Supabase une fois additionnées).
  const recompressIfNeeded=async obj=>{
    const out={};
    for(const[k2,v]of Object.entries(obj||{})){
      if(typeof v==='string'&&v.startsWith('data:image')&&dataUrlSizeKB(v)>250){
        try{out[k2]=await compressToDataUrl(v,{maxDim:1024,quality:0.6});}
        catch(e){console.error('[recompress] failed for',k2,e);out[k2]=v;}
      }else{out[k2]=v;}
    }
    return out;
  };
  const save=async fin=>{
    if(saving)return;
    // Validation des obligatoires uniquement à la soumission finale, pas sur les brouillons
    if(fin){
      setTriedSubmit(true); // déclenche l'affichage rouge des manques sur les items concernés
      if(missing.total>0){
        window.alert(`Impossible de soumettre le rapport : ${missing.total} élément(s) à compléter avant.\n\nLe rapport reste en brouillon. Reviens à l'étape précédente pour compléter ce qui est manquant (signalé en rouge).`);
        setStep(0);
        return;
      }
      // ── Suppléments probablement oubliés (v152) ── Confirmation bloquante, PAS un blocage
      // dur comme les obligatoires ci-dessus : le salarié peut avoir une bonne raison (spa
      // signalé mais finalement pas facturable ce jour-là), donc il garde la main, mais doit
      // le confirmer explicitement plutôt que de soumettre sans y avoir pensé.
      const manquants=supplementsNonCoches(allItems,cks,bien,bill);
      if(manquants.length>0){
        const lignes=manquants.map(m=>`• « ${m.itemLb} » coché → supplément « ${m.bfLb} » PAS coché`).join('\n');
        const continuer=window.confirm(`⚠️ Supplément(s) probablement oublié(s) :\n\n${lignes}\n\nContinuer sans le(s) cocher ?`);
        if(!continuer){setStep(1);return;}
      }
    }
    setSaving(true);
    // ID du rapport — calculé une seule fois (nécessaire pour le chemin Storage ET le brouillon de secours)
    const repId=existing?.id||'rep'+Date.now();
    // Calcul du total facturable (extras ménage) — fait tôt pour être inclus dans le brouillon de secours
    const billTotal=(bien?.bfOn&&Array.isArray(bien?.bf))
      ? bien.bf.reduce((sum,field)=>{
          const v=bill[field.id];
          if(field.t==='n') return sum+(Number(v)||0)*(Number(field.tu)||0);
          return sum+(v===true?(Number(field.tu)||0):0);
        },0)
      : 0;
    // Engagement salarié (v159) : posé UNE FOIS à la vraie première soumission, jamais
    // réécrasé ensuite (même principe que rapportCore.js/ModeGuide.jsx, qui doit rester
    // strictement équivalent — décision d'architecture non négociable de ce projet).
    const engageOut=existing?.engage||((fin&&firstSubmission&&engageRapport)?true:undefined);
    const engageTsOut=existing?.engageTs||((fin&&firstSubmission&&engageRapport)?now():undefined);
    // ─── FILET DE SÉCURITÉ : sauvegarde immédiate d'un brouillon AVANT toute opération risquée ───
    // Si la compression, l'upload ou la géoloc plantent ensuite, le travail (cases, photos, notes) n'est jamais perdu.
    // On sauvegarde les photos telles quelles (dataURL) : elles partiront en Storage à la sauvegarde réussie ou au prochain ré-enregistrement.
    // sub reste null ici : ce n'est qu'un brouillon de secours, la soumission finale se fait plus bas une fois les uploads terminés.
    try{
      const brouillon={id:repId,sid,tid:task?.id,bid:bien?.id,date:today(),cks,pk,gp,bill,billTotal,notes,sub:existing?.sub||null,geo:existing?.geo||null,mrV:useNew?'v2':'v1',engage:engageOut,engageTs:engageTsOut};
      onSave(brouillon,false);
    }catch(e){console.error('[save] brouillon de secours échoué (non bloquant)',e);}
    // ─── FILE D'ATTENTE LOCALE (SPEC_file_locale_v1 §5.2, 2e point d'insertion §10.1) : ───
    // écrite sur le disque AVANT tout réseau. Non bloquant. Confirmation par recordId (§5.3).
    try{
      const qPayload={id:repId,sid,tid:task?.id,bid:bien?.id,date:today(),cks,bill,billTotal,notes,sub:fin?now():(existing?.sub||null),geo:existing?.geo||null,mrV:useNew?'v2':'v1',engage:engageOut,engageTs:engageTsOut};
      await enqueueRapport({recordId:repId,payload:qPayload,pk,gp,sid,authorLabel,fin});
    }catch(e){console.error('[save] enqueue file locale échoué (non bloquant)',e);}
    try{
      // Re-compresser systématiquement avant écriture pour éviter de dépasser la limite Supabase
      const newPk=await recompressIfNeeded(pk);
      const newGp=await recompressIfNeeded(gp);
      // Upload des photos vers Supabase Storage. Remplace chaque dataURL par une URL publique.
      // Les photos déjà en URL (rapport rouvert pour édition) sont laissées telles quelles.
      // Si l'upload échoue pour une photo, on garde son dataURL (la sauvegarde ne plante pas, on perd juste l'avantage taille pour cette photo).
      const storagePk=await uploadPhotosToStorage(newPk,repId,'pk');
      const storageGp=await uploadPhotosToStorage(newGp,repId,'gp');
      // ─── Détection des photos qui n'ont PAS pu partir en Storage (restées en base64 malgré les retries) ───
      // Une photo "partie" a une URL (https://…). Une photo "ratée" reste en "data:image…".
      const countBase64=obj=>Object.values(obj||{}).filter(v=>typeof v==='string'&&v.startsWith('data:image')).length;
      const nonSync=countBase64(storagePk)+countBase64(storageGp);
      // ─── GÉOLOC NON BLOQUANTE ───
      // La géoloc ne doit JAMAIS empêcher la sauvegarde. On l'enveloppe dans une promesse avec
      // timeout court : si le GPS répond, on enregistre sa position ; sinon on enregistre sans (geo:null).
      // Dans tous les cas la sauvegarde a lieu. Évite la "page blanche / rapport non sauvé" si la
      // permission géoloc est refusée ou si getCurrentPosition ne rappelle jamais son callback.
      const geo=await new Promise(resolve=>{
        let done=false;
        const finish=g=>{if(!done){done=true;resolve(g);}};
        // garde-fou : on n'attend pas le GPS plus de 8s
        setTimeout(()=>finish(null),8000);
        try{ req((g)=>finish(g||null)); }
        catch(e){ console.error('[save] géoloc indisponible (non bloquant)',e); finish(null); }
      });
      // Sauvegarde finale, photos uploadées + géoloc (ou null). C'est ici que sub passe à now() si soumission finale.
      const r={id:repId,sid,tid:task?.id,bid:bien?.id,date:today(),cks,pk:storagePk,gp:storageGp,bill,billTotal,notes,sub:fin?now():null,geo:geo||null,mrV:useNew?'v2':'v1',engage:engageOut,engageTs:engageTsOut};
      onSave(r,fin);
      if(fin)onClose();
      // ─── Avertissement salarié si des photos n'ont pas pu être envoyées ───
      // Le rapport est quand même enregistré (les photos sont conservées localement et seront
      // renvoyées au prochain enregistrement depuis un meilleur réseau). On informe clairement.
      if(nonSync>0){
        window.alert(
          `⚠️ ${nonSync} photo(s) n'ont pas pu être envoyées au serveur (connexion internet instable).\n\n`+
          `Ton rapport est bien ENREGISTRÉ et les photos sont conservées.\n`+
          `Dès que tu retrouves une bonne connexion (wifi ou 4G), rouvre ce rapport et ré-enregistre-le : `+
          `les photos partiront automatiquement.\n\n`+
          `La direction est aussi prévenue.`
        );
      }
    }catch(e){
      // ─── CAPTURE D'ERREUR : au lieu de laisser une exception démonter le rendu (page blanche),
      // on l'affiche au salarié et on garantit que le brouillon de secours ci-dessus a bien été enregistré. ───
      console.error('[save] erreur pendant la sauvegarde du rapport',e);
      window.alert(
        `⚠️ Un problème est survenu pendant l'enregistrement des photos (${e&&e.message?e.message:'erreur inconnue'}).\n\n`+
        `Bonne nouvelle : ton rapport a été SAUVEGARDÉ en brouillon, rien n'est perdu.\n`+
        `Rouvre-le quand tu as une bonne connexion et ré-enregistre-le pour finir l'envoi.\n\n`+
        `Si le problème revient, fais une capture de ce message et envoie-la à la direction.`
      );
    }finally{
      setSaving(false);
    }
  };
  const tt=TT[task?.type]||TT.full;
  const STEPS=['Rapport','Facturation'];
  // Vignette photo (réutilisable) — affichée quand une photo est prise
  const Thumb=({src,onClick,onDelete,size=80,border='var(--mn)'})=>(
    <div style={{position:'relative',display:'inline-block'}}>
      <img src={src} alt="" onClick={onClick} style={{width:size,height:size,objectFit:'cover',borderRadius:8,cursor:'pointer',border:`2px solid ${border}`,display:'block'}}/>
      <button type="button" onClick={e=>{e.stopPropagation();onDelete();}} style={{position:'absolute',top:-6,right:-6,width:20,height:20,borderRadius:'50%',background:'var(--dg)',color:'white',border:'2px solid white',fontSize:11,cursor:'pointer',fontWeight:700,lineHeight:1}}>×</button>
    </div>
  );
  // Slot photo vide — bouton "📷" qui ouvre la caméra
  const EmptySlot=({onClick,label,size=70})=>(
    <button type="button" onClick={onClick} style={{width:size,height:size,borderRadius:8,border:'2px dashed var(--li)',background:'var(--bg)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',cursor:'pointer',gap:2}}>
      <span style={{fontSize:22}}>📷</span>
      {label&&<span style={{fontSize:9,color:'var(--si)',fontWeight:600}}>{label}</span>}
    </button>
  );
  return(<div>
    {/* Header */}
    <div style={{background:`linear-gradient(135deg,${tt.bg},var(--wh))`,borderRadius:9,padding:'11px 13px',marginBottom:14,borderLeft:`4px solid ${tt.c}`}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
        <div>
          <div className="D" style={{fontSize:14}}>{bien?.name}</div>
          <div style={{fontSize:11,color:'var(--si)',marginTop:2,display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
            <span>{tt.l}</span>
            {useNew
              ?<span style={{background:'#e1f7ee',color:'#1e6e3d',fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:999,border:'1px solid var(--mn)'}}>✨ MODE PERSONNALISÉ</span>
              :<span style={{background:'var(--bg)',color:'var(--si)',fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:999,border:'1px solid var(--li)'}}>📋 MODE STANDARD</span>
            }
          </div>
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{fontSize:11,fontWeight:700,color:pct===100?'var(--ok)':'var(--or)'}}>{pct}%</div>
          <div style={{width:70,height:4,borderRadius:999,background:'#e0e0e0',marginTop:3,overflow:'hidden'}}><div style={{width:`${pct}%`,height:'100%',background:pct===100?'var(--ok)':'var(--or)',borderRadius:999}}/></div>
        </div>
      </div>
    </div>

    {/* Onglets */}
    <div style={{display:'flex',gap:3,marginBottom:14,overflowX:'auto'}}>{STEPS.map((s,i)=><button key={i} onClick={()=>setStep(i)} className={`st ${step===i?'on':''}`} style={{fontSize:11,padding:'5px 9px',flexShrink:0}}>{s}</button>)}</div>

    {/* Step 0 - Rapport (check-list + photos intégrées) */}
    {step===0&&(<div>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:9,flexWrap:'wrap',gap:6,fontSize:11,color:'var(--si)'}}>
        <span>{checked}/{allItems.length} points validés</span>
        <span>{Object.keys(pk).filter(k=>pk[k]).length}/{photoItems.length} photos points · {Object.keys(gp).filter(k=>gp[k]).length}/{genPhotos.length} photos pièces</span>
      </div>
      <div style={{fontSize:11,color:'var(--si)',fontStyle:'italic',marginBottom:10,padding:'8px 12px',background:'var(--cr)',borderRadius:8}}>💡 Cliquez sur un point avec 📷 pour ouvrir directement l'appareil photo. La case se cochera automatiquement après la prise.</div>
      {firstSubmission&&<div style={{background:'var(--wh)',border:'1px solid var(--li)',borderRadius:8,padding:'9px 12px',marginBottom:10,fontSize:11.5,color:'var(--si)',lineHeight:1.5}}>☑️ Ne cochez une tâche que si vous l'avez réellement effectuée.</div>}

      {sections.map(sec=>{
        const roomId=sec.room?.id;
        const roomGenPhotos=roomId?genPhotos.filter(p=>p._rmId===roomId):[];
        return(<div key={sec.key} style={{marginBottom:14,padding:useNew?'10px 12px':'0',background:useNew?'var(--wh)':'transparent',borderRadius:useNew?10:0,border:useNew?'1px solid var(--li)':'none'}}>
          <div style={{fontSize:useNew?13:10,textTransform:useNew?'none':'uppercase',letterSpacing:useNew?'0':'.08em',fontWeight:700,color:useNew?'var(--or)':'var(--si)',marginBottom:8}}>{sec.title}</div>

          {/* Photos générales de la pièce */}
          {roomGenPhotos.length>0&&(
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:10,padding:'6px 0',alignItems:'center'}}>
              {/* Label "à compléter" si toute la pièce a photoReq actif ET tentative de soumission a eu lieu */}
              {triedSubmit&&roomGenPhotos[0]?.req&&roomGenPhotos.some(p=>!gp[p.id])&&<span style={{fontSize:10,background:'var(--dg)',color:'#fff',padding:'2px 7px',borderRadius:999,fontWeight:700}}>⚠️ à compléter</span>}
              {roomGenPhotos.map((ph,i)=>{
                const src=gp[ph.id];
                const isMissingReq=triedSubmit&&ph.req&&!src;
                return src
                  ?<Thumb key={ph.id} src={src} size={70} onClick={()=>cap('gp',ph.id)} onDelete={()=>delPhoto('gp',ph.id)}/>
                  :<button key={ph.id} type="button" onClick={()=>cap('gp',ph.id)} style={{width:70,height:70,borderRadius:8,border:`2px dashed ${isMissingReq?'var(--dg)':'var(--li)'}`,background:isMissingReq?'#fde0e0':'var(--bg)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',cursor:'pointer',gap:2}}>
                    <span style={{fontSize:22}}>📷</span>
                    <span style={{fontSize:9,color:isMissingReq?'var(--dg)':'var(--si)',fontWeight:isMissingReq?700:600}}>{ph.l}</span>
                  </button>;
              })}
            </div>
          )}

          {/* Items de la pièce */}
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            {sec.items.map(item=>{
              const isChecked=cks[item.id];
              const hasPhoto=item.photo||item.pk;
              const photoSrc=pk[item.id];
              // Obligatoire non rempli : coche manquante OU (photo demandée et photo manquante)
              // → n'apparaît visuellement qu'APRÈS une tentative de soumission (triedSubmit),
              //   pour éviter que les salariés ne cochent QUE ce qui est marqué obligatoire.
              const missCheck=item.req&&!isChecked;
              const missPhoto=item.req&&hasPhoto&&!photoSrc&&isChecked;
              const isMissingReq=triedSubmit&&(missCheck||missPhoto);
              const handleClick=()=>{
                // Click sur item à photo non coché ET sans photo → ouvre la caméra (qui cochera auto)
                if(hasPhoto&&!isChecked&&!photoSrc){cap('pk',item.id);}
                else{setCks(c=>({...c,[item.id]:!c[item.id]}));}
              };
              return(<div key={item.id}>
                <div className={`ci ${isChecked?'ck':''}`} onClick={handleClick} style={isMissingReq?{borderColor:'var(--dg)',background:'#fde0e0'}:undefined}>
                  <div className="cb" style={isMissingReq?{borderColor:'var(--dg)'}:undefined}>{isChecked&&<Ic n="ok" c="w-3.5 h-3.5"/>}</div>
                  <span className="cl" style={{fontSize:13,flex:1,color:isMissingReq?'var(--dg)':undefined,fontWeight:isMissingReq?700:undefined}}>{item.lb}</span>
                  {isMissingReq&&<span style={{fontSize:9,background:'var(--dg)',color:'#fff',padding:'1px 5px',borderRadius:999,fontWeight:700}} title="À compléter avant soumission">⚠️ à compléter</span>}
                  {hasPhoto&&!photoSrc&&<span style={{fontSize:9,background:missPhoto&&triedSubmit?'var(--dg)':'#fff4d6',color:missPhoto&&triedSubmit?'#fff':'#7a5a00',padding:'1px 5px',borderRadius:999,fontWeight:600}}>📷 photo requise</span>}
                  {hasPhoto&&photoSrc&&<span style={{fontSize:9,background:'#e1f7ee',color:'#1e6e3d',padding:'1px 5px',borderRadius:999,fontWeight:600}}>✓📷</span>}
                  {item.ctrl&&<span style={{fontSize:9,background:'#e1f0e5',color:'#1e6e3d',padding:'1px 5px',borderRadius:999,fontWeight:600}}>🔍</span>}
                  {item.std&&<span style={{fontSize:9,background:'#fff4e8',color:'var(--or)',padding:'1px 5px',borderRadius:999,fontWeight:600}} title="Standard propriétaire">⭐</span>}
                </div>
                {hasPhoto&&photoSrc&&(
                  <div style={{marginLeft:32,marginTop:5}}>
                    <Thumb src={photoSrc} size={70} onClick={()=>cap('pk',item.id)} onDelete={()=>delPhoto('pk',item.id)}/>
                  </div>
                )}
                {/* Standard propriétaire (SPEC §12.5, diffusion double) : lecture seule, ne touche pas à la validation du point */}
                {item.std&&(item.std.refPhoto||item.std.consigne)&&(
                  <div style={{marginLeft:32,marginTop:5,padding:'6px 9px',background:'#fff4e8',border:'1px solid #ffd9ad',borderRadius:8,display:'flex',gap:8,alignItems:'center'}}>
                    {item.std.refPhoto&&<img src={item.std.refPhoto} alt="" style={{width:36,height:36,objectFit:'cover',borderRadius:6,flexShrink:0,border:'1.5px solid #fff'}}/>}
                    <span style={{fontSize:11,color:'#a8560f',lineHeight:1.4}}>⭐ {item.std.consigne||'Standard propriétaire — voir photo de référence'}</span>
                  </div>
                )}
              </div>);
            })}
          </div>
        </div>);
      })}

      {/* Photos générales en mode ancien (pas attachées à une pièce) */}
      {!useNew&&genPhotos.length>0&&(
        <div style={{marginTop:14,padding:10,background:'var(--wh)',borderRadius:10,border:'1px solid var(--li)'}}>
          <div style={{fontSize:10,textTransform:'uppercase',letterSpacing:'.08em',fontWeight:700,color:'var(--si)',marginBottom:8}}>📸 Photos générales</div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {genPhotos.map(ph=>{
              const src=gp[ph.id];
              return src
                ?<div key={ph.id} style={{display:'flex',flexDirection:'column',gap:3,alignItems:'center'}}><Thumb src={src} size={80} onClick={()=>cap('gp',ph.id)} onDelete={()=>delPhoto('gp',ph.id)}/><span style={{fontSize:10,color:'var(--si)'}}>{ph.l}</span></div>
                :<div key={ph.id} style={{display:'flex',flexDirection:'column',gap:3,alignItems:'center'}}><EmptySlot onClick={()=>cap('gp',ph.id)} label={null} size={80}/><span style={{fontSize:10,color:'var(--si)'}}>{ph.l}</span></div>;
            })}
          </div>
        </div>
      )}

      <div style={{display:'flex',justifyContent:'space-between',marginTop:14,gap:8,flexWrap:'wrap'}}>
        <button onClick={()=>save(false)} disabled={saving} className="btn bgh sm">{saving?'⏳ Envoi…':'💾 Brouillon'}</button>
        <button onClick={()=>setStep(1)} disabled={saving} className="btn bor">Suivant → Facturation</button>
      </div>
    </div>)}

    {/* Step 1 - Facturation + soumission */}
    {step===1&&(<div>
      {(()=>{
        // Détection du mode facturation paramétrable (nouveau format)
        const bfActive=bien?.bfOn&&Array.isArray(bien?.bf)&&bien.bf.length>0;
        const updNum=(fid,delta)=>{
          const cur=Number(bill[fid])||0;
          const nv=Math.max(0,cur+delta);
          setBill(b=>({...b,[fid]:nv}));
        };
        const setNum=(fid,v)=>{
          const n=Math.max(0,Number(v)||0);
          setBill(b=>({...b,[fid]:n}));
        };
        return(<div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:14}}>
          {bfActive?(<div style={{display:'flex',flexDirection:'column',gap:10}}>
            <div style={{fontSize:11,color:'var(--si)',fontStyle:'italic',padding:'8px 12px',background:'var(--cr)',borderRadius:8}}>💡 Cochez ou indiquez la quantité pour chaque élément qui s'applique à ce ménage. Si rien ne s'applique, laissez tel quel.</div>
            {bien.bf.map(field=>{
              const v=bill[field.id];
              if(field.t==='n'){
                const qty=Number(v)||0;
                return(<div key={field.id} style={{background:'var(--wh)',border:`1.5px solid ${qty>0?'var(--or)':'var(--li)'}`,borderRadius:10,padding:'10px 14px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                  <span style={{fontSize:22}}>{field.em||'💰'}</span>
                  <span style={{flex:1,fontSize:13,fontWeight:600,minWidth:140}}>{field.lb}</span>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <button type="button" onClick={()=>updNum(field.id,-1)} disabled={qty===0} style={{width:34,height:34,borderRadius:'50%',border:'1.5px solid var(--ls)',background:'var(--bg)',cursor:qty===0?'not-allowed':'pointer',fontSize:18,fontWeight:700,opacity:qty===0?.3:1}}>−</button>
                    <input type="number" min="0" value={qty||''} onChange={e=>setNum(field.id,e.target.value)} placeholder="0" style={{width:60,textAlign:'center',fontSize:16,fontWeight:700,padding:'7px 4px'}}/>
                    <button type="button" onClick={()=>updNum(field.id,1)} style={{width:34,height:34,borderRadius:'50%',border:'1.5px solid var(--or)',background:'#fff4e8',cursor:'pointer',fontSize:18,fontWeight:700,color:'var(--or)'}}>+</button>
                  </div>
                </div>);
              }
              // t='b' (case à cocher)
              const checked=v===true;
              return(<div key={field.id} onClick={()=>setBill(b=>({...b,[field.id]:!checked}))} style={{background:checked?'#fff4e8':'var(--wh)',border:`1.5px solid ${checked?'var(--or)':'var(--li)'}`,borderRadius:10,padding:'12px 14px',cursor:'pointer',display:'flex',alignItems:'center',gap:12}}>
                <span style={{fontSize:22}}>{checked?'☑':'☐'}</span>
                <span style={{fontSize:22}}>{field.em||'💰'}</span>
                <span style={{flex:1,fontSize:14,fontWeight:600,color:checked?'var(--or)':'var(--ink)'}}>{field.lb}</span>
              </div>);
            })}
          </div>):(
            <div style={{fontSize:12,color:'var(--si)',fontStyle:'italic',padding:'12px 14px',background:'var(--bg)',borderRadius:8,textAlign:'center'}}>Aucun extra facturable à signaler pour ce bien.</div>
          )}
          <div><label className="fl">Notes libres</label><textarea rows="3" value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Remarques, anomalies, observations…"/></div>
        </div>);
      })()}
      <div style={{background:'#e1f7ee',border:'1.5px solid var(--mn)',borderRadius:9,padding:'10px 13px',marginBottom:12,fontSize:12}}><strong>Résumé :</strong> {checked}/{allItems.length} ✅ · {Object.keys(pk).filter(k=>pk[k]).length}/{photoItems.length} 📷 points · {Object.keys(gp).filter(k=>gp[k]).length}/{genPhotos.length} 🖼 photos pièces</div>
      {/* Bandeau d'éléments manquants — visible UNIQUEMENT après une tentative de soumission échouée.
          Tant que triedSubmit est false, on ne révèle pas au salarié quels éléments sont vérifiés. */}
      {triedSubmit&&missing.total>0&&(
        <div style={{background:'#fde0e0',border:'2px solid var(--dg)',borderRadius:10,padding:'12px 14px',marginBottom:12,display:'flex',gap:10,alignItems:'flex-start'}}>
          <span style={{fontSize:22}}>⚠️</span>
          <div style={{flex:1,fontSize:12,lineHeight:1.6}}>
            <div style={{fontWeight:700,color:'var(--dg)',marginBottom:5}}>Impossible de soumettre : {missing.total} élément(s) à compléter</div>
            {missing.items.length>0&&<div style={{marginBottom:4}}><strong>Coches manquantes :</strong> {missing.items.map(m=>`${m._rmEmoji} ${m.lb}`).join(' · ')}</div>}
            {missing.photos.length>0&&<div style={{marginBottom:4}}><strong>Photos d'items manquantes :</strong> {missing.photos.map(m=>`${m._rmEmoji} ${m.lb}`).join(' · ')}</div>}
            {missing.genPh.length>0&&<div style={{marginBottom:4}}><strong>Photos générales manquantes :</strong> {missing.genPh.map(m=>`${m._rmEmoji} ${m._rmName} (${m.l})`).join(' · ')}</div>}
            <button type="button" onClick={()=>setStep(0)} className="btn bgh sm" style={{marginTop:5,fontSize:11,color:'var(--dg)',borderColor:'var(--dg)'}}>← Retour au rapport pour compléter</button>
          </div>
        </div>
      )}
      {firstSubmission&&<label style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:12,cursor:'pointer',marginBottom:12,background:'var(--wh)',border:'1.5px solid var(--li)',borderRadius:9,padding:'10px 13px'}}>
        <input type="checkbox" checked={engageRapport} onChange={e=>setEngageRapport(e.target.checked)} style={{width:16,height:16,marginTop:2,flexShrink:0}}/>
        <span>Je certifie avoir réellement effectué les tâches cochées dans ce rapport, et j'en assume la responsabilité.</span>
      </label>}
      <div style={{display:'flex',justifyContent:'space-between',flexWrap:'wrap',gap:8}}><button onClick={()=>setStep(0)} disabled={saving} className="btn bgh">← Précédent</button><div style={{display:'flex',gap:8}}><button onClick={()=>save(false)} disabled={saving} className="btn bgh sm">{saving?'⏳ Envoi…':'💾 Brouillon'}</button><button onClick={()=>save(true)} disabled={saving||(triedSubmit&&missing.total>0)||(firstSubmission&&!engageRapport)} className="btn bok" style={(triedSubmit&&missing.total>0)||(firstSubmission&&!engageRapport)?{opacity:.5,cursor:'not-allowed'}:{}}>{saving?'⏳ Envoi des photos…':'✅ Soumettre'}</button></div></div>
    </div>)}
  </div>);
}
